import PopoverItems from '@/components/PopoverItems/PopoverItems';
import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { arrayToStringByComma } from '@/helpers';
import { formatDateRangeText } from '@/helpers/daterange';
import { useGetProductsAndCollectionsQuery } from '@/redux/query';
import {
  pickupCollectionSelector,
  pickupDisableSelector,
  pickupProductSelector,
  pickupSettingSelector,
  pickupStateSelector,
} from '@/redux/slice/pickup.slice';
import { DateRange } from '@/types';
import { IPickupOverrideHolidays, IPickupOverrideSpecificWorkingDays } from '@/types/components/pickup';
import { ActionList, BlockStack, Box, Button, InlineError, InlineGrid, InlineStack, Popover } from '@shopify/polaris';
import { CalendarTimeIcon, DatabaseAddIcon, DeleteIcon, ProductAddIcon } from '@shopify/polaris-icons';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { AvailabilityType } from '@/constants/enum';
import { DateRangePicker } from '@/components';
import { dateRangesOverride, today } from '@/constants/datePreset';

const DateRangeBlock = ({
  dateRules,
  onRemove,
  onAddDateRange,
  onRemoveDateRange,
  onUpdateDateRange,
  onSelectProducts,
  onSelectCollections,
  productsIdChosen,
  collectionsIdChosen,
}: {
  dateRules: IPickupOverrideSpecificWorkingDays | IPickupOverrideHolidays;
  onRemove: () => void;
  onAddDateRange: () => void;
  onRemoveDateRange: (dateRangeIndex: number) => void;
  onUpdateDateRange: (dateRangeIndex: number, updatedDateRange: DateRange) => void;
  onSelectProducts: (filteredProducts: string[]) => void;
  onSelectCollections: (filteredCollections: string[]) => void;
  productsIdChosen: string[];
  collectionsIdChosen: string[];
}) => {
  const pickupSetting = useSelector(pickupSettingSelector);
  const [actionListActive, setActionListActive] = useState<boolean>(false);
  const toggleActive = useCallback(() => setActionListActive((state) => !state), []);
  const pickupProducts = useSelector(pickupProductSelector);
  const pickupCollections = useSelector(pickupCollectionSelector);
  const productsIdLeft = pickupProducts.filter((id) => !productsIdChosen.includes(id) || dateRules.productIds.includes(id));
  const collectionsIdLeft = pickupCollections.filter(
    (id) => !collectionsIdChosen.includes(id) || dateRules.collectionIds.includes(id),
  );
  const { data: productsAndCollectionsData } = useGetProductsAndCollectionsQuery({
    shop: config.shop,
    productIds: arrayToStringByComma(dateRules.productIds),
    collectionIds: arrayToStringByComma(dateRules.collectionIds),
  });
  const pickupState = useSelector(pickupStateSelector);
  const pickupDisable = useSelector(pickupDisableSelector);

  const blockProducts = useMemo(
    () =>
      productsAndCollectionsData?.data.products.map((item) => ({
        title: item?.title,
        imgSrc: item?.featuredMedia?.preview?.image?.url,
      })),
    [productsAndCollectionsData?.data.products],
  );

  const blockCollections = useMemo(
    () => productsAndCollectionsData?.data.collections.map((item) => ({ title: item?.title, imgSrc: item?.image?.url })),
    [productsAndCollectionsData?.data.collections],
  );

  const activator = (
    <Button onClick={toggleActive} disabled={pickupDisable.isDisableProductOverride} disclosure>
      Actions
    </Button>
  );

  const header = useMemo(() => {
    if (dateRules.productIds.length && dateRules.collectionIds.length) {
      return (
        <InlineStack gap={'100'} blockAlign="end">
          <RegularText>Selected</RegularText>
          <PopoverItems
            title={`${dateRules.productIds?.length} product${dateRules.productIds?.length > 1 ? 's' : ''}`}
            items={blockProducts || []}
          />
          <RegularText>and</RegularText>
          <PopoverItems
            title={`${dateRules.collectionIds?.length} collection${dateRules.collectionIds?.length > 1 ? 's' : ''}`}
            items={blockCollections || []}
          />
        </InlineStack>
      );
    } else if (dateRules.productIds.length) {
      return (
        <PopoverItems
          title={`${dateRules.productIds.length} product${dateRules.productIds.length > 1 ? 's' : ''} selected`}
          items={blockProducts || []}
        />
      );
    } else if (dateRules.collectionIds.length) {
      return (
        <PopoverItems
          title={`${dateRules.collectionIds.length} collection${dateRules.collectionIds.length > 1 ? 's' : ''} selected`}
          items={blockCollections || []}
        />
      );
    }
    return 'No products or collections selected';
  }, [blockCollections, blockProducts, dateRules.collectionIds, dateRules.productIds]);

  return (
    <Box paddingInlineStart="200" borderInlineStartWidth="050">
      <BlockStack gap="200">
        <InlineGrid columns="1fr auto">
          <RegularText>{header}</RegularText>
          <Popover
            active={actionListActive}
            activator={activator}
            autofocusTarget="first-node"
            onClose={() => setActionListActive(false)}
          >
            <ActionList
              actionRole="menuitem"
              sections={[
                {
                  items: [
                    {
                      content: 'Select product',
                      onAction: () => {
                        toggleActive();
                        onSelectProducts(productsIdLeft);
                      },
                      disabled:
                        pickupSetting.productAvailabilityType !== AvailabilityType.ALL &&
                        (!pickupProducts.length || !productsIdLeft.length),
                      icon: ProductAddIcon,
                    },
                    {
                      content: 'Select collection',
                      onAction: () => {
                        toggleActive();
                        onSelectCollections(collectionsIdLeft);
                      },
                      disabled:
                        pickupSetting.productAvailabilityType !== AvailabilityType.ALL &&
                        (!pickupCollections.length || !collectionsIdLeft.length),
                      icon: DatabaseAddIcon,
                    },
                    {
                      content: 'Add date range',
                      onAction: () => {
                        toggleActive();
                        onAddDateRange();
                      },
                      icon: CalendarTimeIcon,
                    },
                    {
                      destructive: true,
                      content: 'Remove',
                      onAction: () => {
                        toggleActive();
                        onRemove();
                      },
                      icon: DeleteIcon,
                    },
                  ],
                },
              ]}
            />
          </Popover>
        </InlineGrid>
        <BlockStack gap="200">
          {dateRules.overrideDates.map((item, index) => (
            <Box key={`date-range-${index}`} background="bg-surface-selected" padding="200" borderRadius="100">
              <InlineGrid columns="1fr auto">
                <InlineStack gap="200" blockAlign="center">
                  <DateRangePicker
                    disableDatesBefore={today}
                    disabledFeature={pickupDisable.isDisableProductOverride}
                    datePresets={dateRangesOverride}
                    range={{ since: item.startDate, until: item.endDate }}
                    updateDateRange={(updatedDateRange) => onUpdateDateRange(index, updatedDateRange)}
                    defaultText="Select date range"
                  />
                  {item.startDate && item.endDate && (
                    <RegularText>{formatDateRangeText(item.startDate, item.endDate)}</RegularText>
                  )}
                </InlineStack>
                <Button
                  tone="critical"
                  icon={DeleteIcon}
                  size="slim"
                  disabled={pickupDisable.isDisableProductOverride}
                  onClick={() => onRemoveDateRange(index)}
                />
              </InlineGrid>
            </Box>
          ))}
          {pickupState.isSaving &&
            (!dateRules.overrideDates.length || dateRules.overrideDates.some((item) => !item.startDate || !item.endDate)) && (
              <InlineError message="Please select a valid date range" fieldID="" />
            )}
          {pickupState.isSaving &&
            !!dateRules.overrideDates.length &&
            !dateRules.overrideDates.some((item) => !item.startDate || !item.endDate) &&
            !dateRules.productIds.length &&
            !dateRules.collectionIds.length && (
              <InlineError message={'Please select at least one product or collection'} fieldID="" />
            )}
        </BlockStack>
      </BlockStack>
    </Box>
  );
};

export default DateRangeBlock;
